import { FC, useContext, useEffect, useRef, useState } from 'react';

import {
  BackwardIcon,
  BottomIcon,
  Button,
  DotsMenuIcon,
  H1,
  H2,
  OpenedEyeIcon,
  PosetiteliIcon,
  Separator,
  Tooltip,
  TopIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { useAdvertisementMutations, useGetAdvertisementContact } from 'hooks';
import { LocalizationContext } from 'localization';
import { AdvertisementUnpublishedModal } from 'modals';
import { openConfirmDialog } from 'services/Confirmation';
import {
  EAdvertisementStatus,
  IMyAdvertisement,
  IMyAdvertisementMetrics,
} from 'types';
import { formatCompactNumber, formatExpirationDate } from 'utils';
import { getAdvertisementImageUrl } from 'utils/advertisementImage';

import s from './AdvertisementViewMobile.module.scss';
import { IAdvertisementViewModalProps } from './interfaces';
import {
  getActionButton,
  getDropdownTextAction,
  getStatusLabel,
} from './utils';

export const AdvertisementViewMobile: FC<IAdvertisementViewModalProps> = ({
  onClose,
  mode,
  advertisement,
  setAdvertisementManagerState,
  setAdvertisementViewState,
}) => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [openUnpublishedModal, setOpenUnpublishedModal] =
    useState<boolean>(false);

  useEffect(() => {
    if (textRef.current) {
      const isOverflowing = textRef.current.scrollHeight > 210;
      setShouldShowButton(isOverflowing);

      if (!isOverflowing) {
        setIsTextExpanded(false);
      }
    }
  }, [advertisement?.description]);

  const toggleTextExpand = (): void => {
    setIsTextExpanded(!isTextExpanded);
  };
  const toggleDropdown = (): void => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;

      const clickedInsideDropdown = dropdownRef.current?.contains(target);

      const clickedButton = buttonRef.current?.contains(target);

      if (!clickedInsideDropdown && !clickedButton) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const {
    handledGetContact,
    contact,
    loading: showContactLoading,
  } = useGetAdvertisementContact();

  const {
    handledDeleteAdvertisement,
    deleting,
    handledChangeAdvertisementStatus,
    changingStatus,
    handledRepublishAdvertisement,
    republishing,
  } = useAdvertisementMutations(t);

  const disabled = !permissions?.has('advertisement_manage');

  useEffect(() => {
    const mainElement = document.getElementById('main-content');
    if (mainElement) {
      mainElement.scrollTo({
        top: 0,
      });
    }
  }, []);

  return (
    <div className={s.container}>
      {openUnpublishedModal && (
        <AdvertisementUnpublishedModal
          onClose={() => setOpenUnpublishedModal(false)}
          handledChangeAdvertisementStatus={handledChangeAdvertisementStatus}
          changingStatus={changingStatus}
          data={advertisement as IMyAdvertisement}
          closeViewModal={onClose}
        />
      )}
      {isDropdownOpen && <div className={s.mobile_frame}></div>}

      <div className={s.link} onClick={() => setAdvertisementViewState(null)}>
        <BackwardIcon />
        {t('back')}
      </div>
      <div className={s.content}>
        {mode === 'my' &&
          permissions?.has('advertisement_manage') &&
          getStatusLabel(t, lang, advertisement as IMyAdvertisement)}
        {advertisement?.images.length && advertisement?.images.length > 1 && (
          <div className={s.carusel}>
            <div className={s.view_block}>
              <OpenedEyeIcon />
              <span className={s.text}>
                {formatCompactNumber(advertisement?.metrics?.views, t)}
              </span>
            </div>
            {advertisement.images.map((image, index) => (
              <div
                key={`${image}-${index}`}
                className={s.blur}
                style={{
                  backgroundImage: `url(${getAdvertisementImageUrl(
                    image,
                    264
                  )})`,
                }}
                onClick={() => {
                  window.open(getAdvertisementImageUrl(image), '_blank');
                }}
              >
                <img
                  src={getAdvertisementImageUrl(image, 264)}
                  srcSet={`
                          ${getAdvertisementImageUrl(image, 264)} 1x,
                          ${getAdvertisementImageUrl(image, 528)} 2x,
                          ${getAdvertisementImageUrl(image, 792)} 3x,
                        `}
                  alt={image}
                  className={s.image}
                />
              </div>
            ))}
          </div>
        )}
        {advertisement?.images.length && advertisement?.images.length === 1 && (
          <div className={s.carusel}>
            <div className={s.view_block}>
              <OpenedEyeIcon />
              <span className={s.text}>
                {formatCompactNumber(advertisement?.metrics?.views, t)}
              </span>
            </div>
            {advertisement.images.map((image, index) => (
              <div
                key={`${image}-${index}`}
                className={cn(s.blur, s.blur_one)}
                style={{
                  backgroundImage: `url(${getAdvertisementImageUrl(
                    image,
                    264
                  )})`,
                }}
                onClick={() => {
                  window.open(image, '_blank');
                }}
              >
                <img
                  src={getAdvertisementImageUrl(image, 264)}
                  srcSet={`
                          ${getAdvertisementImageUrl(image, 264)} 1x,
                          ${getAdvertisementImageUrl(image, 528)} 2x,
                          ${getAdvertisementImageUrl(image, 792)} 3x,
                        `}
                  alt={image}
                  className={s.image}
                />
              </div>
            ))}
          </div>
        )}
        <div className={s.frame}>
          <div className={s.main_info}>
            <div className={s.title_block}>
              <H1 className={s.title}>{advertisement?.title}</H1>
              <span className={s.text_secondary}>
                {advertisement?.category?.title} ·{' '}
                {formatExpirationDate(advertisement?.published_at, lang)}
              </span>
            </div>

            <span className={s.cost}>
              {advertisement?.price} {advertisement?.currency?.letter}
            </span>

            {mode === 'my' && permissions?.has('advertisement_manage') ? (
              <>
                <div className={s.contacts_block}>
                  <H2>{t('contacts')}</H2>
                  <div className={s.info}>
                    <span>{(advertisement as IMyAdvertisement)?.contact}</span>
                  </div>
                </div>
                <div className={s.statistics}>
                  <span className={s.title}>{t('statistics')}</span>
                  <div className={s.info}>
                    <div className={s.info_block}>
                      <div className={s.icon}>
                        <OpenedEyeIcon size="s" color="secondary" />
                      </div>
                      <div className={s.text_block}>
                        <span className={s.title}>{t('views')}</span>
                        <span className={s.value}>
                          {advertisement
                            ? formatCompactNumber(
                                advertisement.metrics.views,
                                t
                              )
                            : ''}
                        </span>
                      </div>
                    </div>
                    <div className={s.info_block}>
                      <div className={s.icon}>
                        <PosetiteliIcon size="s" color="secondary" />
                      </div>
                      <div className={s.text_block}>
                        <div className={cn(s.title, s.title_tooltip)}>
                          <span>{t('contacts.requests')}</span>
                          <Tooltip
                            position="top-right"
                            content={t('contacts.requests.tooltip')}
                          />
                        </div>
                        <span className={s.value}>
                          {advertisement
                            ? formatCompactNumber(
                                (
                                  advertisement?.metrics as IMyAdvertisementMetrics
                                )?.request_contact,
                                t
                              )
                            : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <footer className={s.footer}>
                {!contact && (
                  <div className={s.contacts}>
                    <Button
                      onClick={() => {
                        if (advertisement) {
                          handledGetContact(advertisement?.id);
                        }
                      }}
                      isLoading={showContactLoading}
                      className={s.button}
                    >
                      {t('show.contacts')}
                    </Button>
                  </div>
                )}

                {contact && !showContactLoading && (
                  <div className={s.contacts_view}>
                    <div className={s.info}>
                      <span>{contact}</span>
                    </div>
                  </div>
                )}
              </footer>
            )}
          </div>
        </div>

        <div className={s.frame}>
          <span className={s.text_secondary}>
            {t('advertisement.manager.form.description')}
          </span>
          <div
            ref={textRef}
            className={s.textWrapper}
            style={{ maxHeight: isTextExpanded ? 'none' : '210px' }}
          >
            <p className={s.text}>{advertisement?.description}</p>
          </div>

          {shouldShowButton && (
            <button className={s.show_button} onClick={toggleTextExpand}>
              <span>{isTextExpanded ? t('hidden') : t('show.more')}</span>
              {isTextExpanded ? <TopIcon /> : <BottomIcon />}
            </button>
          )}
        </div>

        {mode === 'my' && permissions?.has('advertisement_manage') ? (
          <div className={cn(s.vendor, s.vendor_my)}>
            <span className={s.vendor_text}>
              {advertisement?.company?.name}
            </span>
            <span className={s.text_secondary}>
              {(advertisement as IMyAdvertisement)?.company?.city}
            </span>
          </div>
        ) : (
          <div className={s.vendor}>
            <span className={s.text_secondary}>{t('vendor')}</span>
            <span className={s.vendor_text}>
              {advertisement?.company?.name}
            </span>
          </div>
        )}
      </div>

      {mode === 'my' && permissions?.has('advertisement_manage') && (
        <div className={s.footer}>
          {getActionButton(
            t,
            setAdvertisementManagerState,
            setAdvertisementViewState,
            setOpenUnpublishedModal,
            handledRepublishAdvertisement,
            advertisement as IMyAdvertisement,
            deleting || changingStatus || republishing,
            onClose,
            disabled
          )}
          <div className={s.actions}>
            <button
              className={s.button}
              ref={buttonRef}
              disabled={!permissions?.has('advertisement_manage')}
              onClick={toggleDropdown}
            >
              <DotsMenuIcon color="link-info" />
            </button>
            {isDropdownOpen && (
              <ul className={cn(s.dropdown)} ref={dropdownRef}>
                {(advertisement as IMyAdvertisement)?.status !==
                  EAdvertisementStatus.REJECTED &&
                  (advertisement as IMyAdvertisement)?.status !==
                    EAdvertisementStatus.MODERATION && (
                    <li
                      className={s.item}
                      onClick={(e) => {
                        e.stopPropagation();

                        if (
                          (advertisement as IMyAdvertisement)?.status ===
                          'ACTIVE'
                        ) {
                          setOpenUnpublishedModal(true);
                          setIsDropdownOpen(false);
                        } else if (
                          (advertisement as IMyAdvertisement)?.status ===
                            EAdvertisementStatus.COMPLETED ||
                          (advertisement as IMyAdvertisement)?.status ===
                            EAdvertisementStatus.REJECTED ||
                          (advertisement as IMyAdvertisement)?.status ===
                            EAdvertisementStatus.EXPIRED
                        ) {
                          handledRepublishAdvertisement(
                            (advertisement as IMyAdvertisement)?.id
                          ).then(() => onClose());
                        }
                      }}
                    >
                      {getDropdownTextAction(
                        t,
                        (advertisement as IMyAdvertisement)?.status
                      )}
                    </li>
                  )}
                <li
                  className={s.item}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAdvertisementViewState(null);

                    setAdvertisementManagerState({
                      mode: 'edit',
                      advertisement: advertisement as IMyAdvertisement,
                    });
                  }}
                >
                  {t('edit')}
                </li>
                <Separator className={s.separator} />
                <li
                  className={cn(s.item, s.red)}
                  onClick={(e) => {
                    e.stopPropagation();

                    if (advertisement) {
                      setIsDropdownOpen(false);

                      openConfirmDialog(t, 'remove', {
                        title: t('advertisement.delete.title'),
                        message: t(
                          'advertisement.delete.description',
                          advertisement?.title
                        ),
                        acceptTitle: t('delete'),
                        declineTitle: t('cancel'),
                      }).then(({ status }) => {
                        if (status === 'ACCEPTED') {
                          handledDeleteAdvertisement(advertisement?.id).then(
                            () => {
                              onClose();
                            }
                          );
                        }
                      });
                    }
                  }}
                >
                  {t('delete')}
                </li>
                <Separator className={s.separator_mobile} />
                <li
                  className={cn(s.item, s.item_mobile)}
                  onClick={(e) => {
                    e.stopPropagation();

                    setIsDropdownOpen(false);
                  }}
                >
                  {t('cancel_2')}
                </li>
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
